import React, { useState, useEffect, useRef } from 'react';
import * as turf from '@turf/turf';
import { Box, IconButton, Tooltip, Slider, Typography, Paper } from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  ViewInAr as View3DIcon,
  ViewModule as View2DIcon,
  Navigation as CompassIcon
} from '@mui/icons-material';
import Map, { MapRef, Source, Layer, Marker } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import EnhancedPermanentLayers from './EnhancedPermanentLayers';
import { API_BASE_URL } from '../config';

import { useMap, BasemapType } from '../context/MapContext';
import SatelliteInfo from './SatelliteInfo';

// Your Mapbox access token
const MAPBOX_TOKEN = "pk.eyJ1IjoiZW1pbmJvdWthIiwiYSI6ImNtZGdvaXp3MzAyZmgybXM2MWEwNnc4cGQifQ.qUIaXbqY_p32IEGF3vaXhA";

interface ImportedLayer {
  id: string;
  name: string;
  geojson: GeoJSON.FeatureCollection;
  visible: boolean;
}

interface MapboxMapProps {
  onModeChange: (mode: 'line' | 'polyline' | 'area') => void;
  onMeasureResult?: (result: string) => void;
  onMeasureClose?: () => void;
  mapContainerId?: string;
  center: [number, number];
  zoom: number;
  onMapLoad?: (map: mapboxgl.Map) => void;
  onZoomChange: (zoom: number) => void;
  importedLayers: ImportedLayer[];

  layers: import('../data/layersConfig').LayerConfig[];
  manualGeometry?: GeoJSON.Geometry | null;
  manualZoomRequest?: number;
  onManualZoomComplete?: () => void; // Add this prop
  measureActive: boolean;
  measureMode: 'line' | 'polyline' | 'area';
  findXYActive: boolean;
  showImportedFilePopup: boolean;
  selectedImportedLayerId: string | null;
  onLayerSelect: (id: string | null) => void;
  onPopupClose: () => void;

  pins: { lat: string; lng: string; id: string }[];
  tempMarker: { lat: number; lng: number } | null;
  areaMarkers?: Array<{ lat: number; lng: number; id: string }>; // Add area markers prop
  onMapClick?: (e: mapboxgl.MapMouseEvent) => void;
  onMapMove?: (e: any) => void;
  flyToTarget?: { lat: number; lng: number } | null;
  flyToTrigger?: boolean;
  flyToZoom?: number | null;
  children?: React.ReactNode;
  permanentLayers?: Array<any>;
  visibleLayers?: string[];
  mapContainerRef?: React.RefObject<HTMLDivElement>; // Add this prop
  labelsVisible?: boolean;
  scrollZoomEnabled?: boolean;
  boxZoomActive?: boolean;
  layerActionPanelOpen?: boolean; // Add this
  onLayerActionClose?: () => void; // Add this
}

export interface MapboxMapHandle {
  clearMeasure: () => void;
}

const MapboxMap = React.forwardRef<MapboxMapHandle, MapboxMapProps>(({
  onMeasureResult,
  center,
  zoom,
  onMapLoad,
  onZoomChange,
  onMapClick,
  onMapMove,
  flyToTarget,
  flyToTrigger,
  flyToZoom,
  importedLayers = [],
  measureActive,
  measureMode,
  pins,
  tempMarker,
  areaMarkers = [],
  mapContainerId = 'map',
  children,
  manualGeometry,
  manualZoomRequest,
  permanentLayers = [],
  visibleLayers = [],
  mapContainerRef,
  onManualZoomComplete,
  labelsVisible = true,
  scrollZoomEnabled = true,
  boxZoomActive = false,
  layerActionPanelOpen = false,
  onLayerActionClose = () => { },
  ...otherProps
}, ref) => {
  const { basemap, satelliteMode } = useMap(); // Get from MapContext
  const mapRef = useRef<MapRef>(null);


  // Function to get basemap style based on type
  const getBasemapStyle = (type: BasemapType): string => {
    switch (type) {
      case 'mapbox-streets':
        return 'mapbox://styles/mapbox/streets-v12';
      case 'ign-1993':
        return 'mapbox://styles/mapbox/light-v11'; // Use light style as base, we'll add IGN tileset as overlay
      case 'mapbox-outdoors':
        return 'mapbox://styles/mapbox/outdoors-v11';
      case 'satellite-a':
        return 'mapbox://styles/mapbox/satellite-streets-v12'; // Satellite with labels
      case 'satellite-b':
        return 'mapbox://styles/mapbox/satellite-v9'; // Pure satellite
      case 'satellite-c':
        return 'mapbox://styles/mapbox/satellite-v9'; // Pure satellite (base for enhanced)
      case 'satellite-enhanced':
        return 'mapbox://styles/mapbox/satellite-v9'; // Enhanced satellite (will add ESRI overlay)
      // GEE basemaps - use light style as base, GEE layers will be added as overlays
      case 'gee-sentinel-2':
      case 'gee-landsat-8':
      case 'gee-landsat-9':
      case 'gee-modis':
      case 'gee-spot':
        return 'mapbox://styles/mapbox/light-v11';
      // 2025 High-resolution satellite providers - use satellite style as base
      case 'maxar-worldview':
        return 'mapbox://styles/mapbox/satellite-v9'; // Will add Maxar WorldView as overlay
      case 'airbus-pleiades':
        return 'mapbox://styles/mapbox/satellite-v9'; // Will add Airbus Pléiades as overlay
      case 'planet-skybox':
        return 'mapbox://styles/mapbox/satellite-v9'; // Will add Planet SkySat as overlay
      case 'blacksky-constellation':
        return 'mapbox://styles/mapbox/satellite-v9'; // Will add BlackSky as overlay
      // SAR imagery providers - use dark style as base for radar imagery
      case 'capella-sar':
      case 'iceye-sar':
        return 'mapbox://styles/mapbox/dark-v11'; // Dark base for SAR imagery
      // Latest Earth observation satellites
      case 'sentinel-3':
      case 'sentinel-6':
        return 'mapbox://styles/mapbox/satellite-v9'; // Will add Sentinel data as overlay
      case 'landsat-10':
        return 'mapbox://styles/mapbox/satellite-v9'; // Will add Landsat 10 as overlay
      // MODIS 2025 collections
      case 'modis-terra-2025':
      case 'modis-aqua-2025':
        return 'mapbox://styles/mapbox/light-v11'; // Light base for MODIS overlays
      default:
        return 'mapbox://styles/mapbox/streets-v12';
    }
  };


  // const drawRef = useRef<any>(null); // Unused - keeping for potential future use
  const [viewState, setViewState] = useState({ longitude: center[0], latitude: center[1], zoom });
  const [distanceResults, setDistanceResults] = useState<{ id: number; value: string }[]>([]);
  // const [measureResult, setMeasureResult] = useState<string | null>(null); // Unused
  const [nextResultId, setNextResultId] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [distanceHasStarted, setDistanceHasStarted] = useState(false);
  const [manualGeoJson, setManualGeoJson] = useState<GeoJSON.FeatureCollection>({ type: 'FeatureCollection', features: [] });
  const [importedLayersData, setImportedLayersData] = useState<ImportedLayer[]>([]);

  // Custom box zoom state
  const [isBoxZooming, setIsBoxZooming] = useState(false);
  const [boxZoomStart, setBoxZoomStart] = useState<{ x: number; y: number } | null>(null);
  const [boxZoomEnd, setBoxZoomEnd] = useState<{ x: number; y: number } | null>(null);
  const [boxZoomRect, setBoxZoomRect] = useState<HTMLDivElement | null>(null);

  // Custom box zoom handlers
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    const container = map.getContainer();

    let rect: HTMLDivElement | null = null;
    let start: { x: number; y: number } | null = null;

    function onMouseDown(e: MouseEvent) {
      if (!boxZoomActive) return;
      if (e.button !== 0) return; // Only left click
      e.preventDefault();
      start = { x: e.clientX, y: e.clientY };
      setIsBoxZooming(true);
      setBoxZoomStart({ x: e.clientX, y: e.clientY });
      setBoxZoomEnd({ x: e.clientX, y: e.clientY });

      rect = document.createElement('div');
      rect.style.position = 'absolute';
      rect.style.border = '2px dashed #2c8fdb';
      rect.style.background = 'rgba(44,143,219,0.08)';
      rect.style.pointerEvents = 'none';
      rect.style.zIndex = '9999';
      container.appendChild(rect);
      setBoxZoomRect(rect);

      function onMouseMove(ev: MouseEvent) {
        if (!start || !rect) return;
        const x1 = Math.min(start.x, ev.clientX);
        const y1 = Math.min(start.y, ev.clientY);
        const x2 = Math.max(start.x, ev.clientX);
        const y2 = Math.max(start.y, ev.clientY);
        rect.style.left = x1 - container.getBoundingClientRect().left + 'px';
        rect.style.top = y1 - container.getBoundingClientRect().top + 'px';
        rect.style.width = x2 - x1 + 'px';
        rect.style.height = y2 - y1 + 'px';
        setBoxZoomEnd({ x: ev.clientX, y: ev.clientY });
      }

      function onMouseUp(ev: MouseEvent) {
        if (!start || !rect || !map) return;
        setIsBoxZooming(false);
        setBoxZoomStart(null);
        setBoxZoomEnd(null);
        rect.remove();
        setBoxZoomRect(null);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        // Calculate bounds and zoom
        const startPoint = map.unproject([start.x - container.getBoundingClientRect().left, start.y - container.getBoundingClientRect().top]);
        const endPoint = map.unproject([ev.clientX - container.getBoundingClientRect().left, ev.clientY - container.getBoundingClientRect().top]);
        const bounds = new mapboxgl.LngLatBounds()
          .extend([startPoint.lng, startPoint.lat])
          .extend([endPoint.lng, endPoint.lat]);
        if (Math.abs(ev.clientX - start.x) > 10 && Math.abs(ev.clientY - start.y) > 10) {
          map.fitBounds(bounds, { padding: 20, duration: 500 });
        }
      }

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    if (boxZoomActive) {
      map.boxZoom.disable();
      container.style.cursor = 'crosshair';
      container.addEventListener('mousedown', onMouseDown);
    } else {
      map.boxZoom.enable();
      container.style.cursor = '';
      if (boxZoomRect) {
        boxZoomRect.remove();
        setBoxZoomRect(null);
      }
    }
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      if (boxZoomRect) {
        boxZoomRect.remove();
        setBoxZoomRect(null);
      }
      container.style.cursor = '';
    };
    // eslint-disable-next-line
  }, [boxZoomActive]);

  // const onResult = (result: string) => { // Unused
  //         if (measureMode === 'line') {
  //     // Handle empty result (Clear button pressed)
  //     if (!result) {
  //       setMeasureResult(null);
  //       setIsDrawing(false);
  //       setDistanceHasStarted(false);
  //       return;
  //     }
  //     
  //     // Handle completed measurement
  //     if (result && isDrawing) {
  //       setDistanceResults(prev => [{ id: nextResultId, value: result }, ...prev]);
  //       setNextResultId(id => id + 1);
  //       setMeasureResult('');
  //       setIsDrawing(false);
  //       setDistanceHasStarted(false);
  //     } else if (result) {
  //       // Handle in-progress measurement
  //       if (!distanceHasStarted) {
  //         setDistanceHasStarted(true);
  //         setIsDrawing(true);
  //       }
  //       setMeasureResult(result);
  //     }
  //   } else {
  //     if (onMeasureResult) onMeasureResult(result);
  //   }
  // };

  // Get map instance safely with useCallback to prevent recreating references
  const getMapInstance = () => mapRef.current?.getMap();

  // Fix the useMapboxDraw hook usage
  // Remove the useMapboxDraw hook call entirely
  // const { clearDrawing } = useMapboxDraw({
  //   getMapInstance: () => getMapInstance() || null, // Use the new interface
  //   measureMode,
  //   onMeasureResult: (results) => {
  //     // Handle measure results
  //     console.log('Measure results:', results);
  //   },
  //   isDrawing,
  //   distanceHasStarted,
  //   onDrawingChange: setIsDrawing,
  //   onDistanceStartedChange: setDistanceHasStarted
  // });

  React.useImperativeHandle(ref, () => ({
    clearMeasure() {
      // Remove the clearDrawing call since it's handled in App_Mapbox
      setDistanceResults([]);
    }
  }), []);

  const onMapLoadHandler = (evt: any) => {
    if (onMapLoad) onMapLoad(evt.target);
  };

  const onMove = (evt: any) => {
    onZoomChange(evt.viewState.zoom);
  };

  // 3D Navigation Control Component
  const Box3DNavigationControl = ({ getMapInstance }: { getMapInstance: () => mapboxgl.Map | null | undefined }) => {
    const [bearing, setBearing] = useState(0);
    const [pitch, setPitch] = useState(0);
    const [is3DMode, setIs3DMode] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const [scale, setScale] = useState<string>('');

    // Rotation animation state
    const [isRotating, setIsRotating] = useState(false);
    const rotationRef = useRef<number | null>(null);
    const isRotatingRef = useRef(false);

    // Start 360° rotation animation
    const startRotation = () => {
      const map = getMapInstance();
      if (!map || isRotatingRef.current) return;

      isRotatingRef.current = true;
      setIsRotating(true);
      map.setPitch(60); // Set pitch to 60 degrees for 3D view
      setPitch(60);

      const rotate = () => {
        if (!isRotatingRef.current) return;
        const currentBearing = map.getBearing();
        const newBearing = (currentBearing + 0.1) % 360;
        map.setBearing(newBearing);
        setBearing(newBearing);
        rotationRef.current = requestAnimationFrame(rotate);
      };

      rotate();
    };

    // Stop rotation animation
    const stopRotation = () => {
      isRotatingRef.current = false;
      setIsRotating(false);
      if (rotationRef.current) {
        cancelAnimationFrame(rotationRef.current);
        rotationRef.current = null;
      }
    };

    const handleResetBearing = () => {
      const map = getMapInstance();
      if (map) {
        stopRotation(); // Stop rotation if active
        map.easeTo({ bearing: 0, duration: 1000 });
        setBearing(0);
      }
    };

    const handleToggle3D = () => {
      const map = getMapInstance();
      if (map) {
        if (is3DMode) {
          // Switch to 2D - stop rotation and reset
          stopRotation();
          map.easeTo({ pitch: 0, bearing: 0, duration: 1000 });
          setPitch(0);
          setBearing(0);
          setIs3DMode(false);
        } else {
          // Switch to 3D - start rotation
          setIs3DMode(true);
          // small async to ensure state flush before RAF loop
          setTimeout(startRotation, 0);
        }
      }
    };

    const handleBearingChange = (event: Event, newValue: number | number[]) => {
      const map = getMapInstance();
      if (map) {
        stopRotation(); // Stop rotation when user manually adjusts bearing
        const bearingValue = newValue as number;
        map.easeTo({ bearing: bearingValue, duration: 300 });
        setBearing(bearingValue);
      }
    };

    const handlePitchChange = (event: Event, newValue: number | number[]) => {
      const map = getMapInstance();
      if (map) {
        stopRotation(); // Stop rotation when user manually adjusts pitch
        const pitchValue = newValue as number;
        map.easeTo({ pitch: pitchValue, duration: 300 });
        setPitch(pitchValue);
      }
    };

    const handleZoomIn = () => {
      const map = getMapInstance();
      if (map) {
        map.zoomIn();
      }
    };

    const handleZoomOut = () => {
      const map = getMapInstance();
      if (map) {
        map.zoomOut();
      }
    };

    // Update bearing, pitch, and scale from map events
    useEffect(() => {
      const map = getMapInstance();
      if (!map) return;

      const updateFromMap = () => {
        setBearing(map.getBearing());
        setPitch(map.getPitch());
        setIs3DMode(map.getPitch() > 0);

        // Update scale
        const center = map.getCenter();
        const zoom = map.getZoom();

        // More accurate scale calculation using Web Mercator projection
        const lat = center.lat * Math.PI / 180;
        const metersPerPixel = (156543.03392 * Math.cos(lat)) / Math.pow(2, zoom);

        // Calculate scale for a 100-pixel bar
        const scaleMeters = metersPerPixel * 100;

        let scaleText = '';
        if (scaleMeters >= 1000) {
          // For km values, round to nearest 0.1 km
          const kmValue = Math.round(scaleMeters / 100) / 10;
          scaleText = `${kmValue} km`;
        } else if (scaleMeters >= 100) {
          // For meters >= 100, round to nearest 100m
          const metersValue = Math.round(scaleMeters / 100) * 100;
          scaleText = `${metersValue}m`;
        } else {
          // For meters < 100, round to nearest 10m
          const metersValue = Math.round(scaleMeters / 10) * 10;
          scaleText = `${metersValue}m`;
        }

        setScale(scaleText);
      };

      // Stop rotation on user interaction
      const handleUserInteraction = () => {
        if (isRotatingRef.current) {
          stopRotation();
        }
      };

      // Use 'moveend' and 'zoomend' instead of 'move' and 'zoom' to reduce frequent updates
      map.on('moveend', updateFromMap);
      map.on('zoomend', updateFromMap);

      // Stop rotation on user interaction events
      map.on('mousedown', handleUserInteraction);
      map.on('touchstart', handleUserInteraction);
      map.on('wheel', handleUserInteraction);
      map.on('dragstart', handleUserInteraction);

      updateFromMap(); // Initial update

      return () => {
        map.off('moveend', updateFromMap);
        map.off('zoomend', updateFromMap);
        map.off('mousedown', handleUserInteraction);
        map.off('touchstart', handleUserInteraction);
        map.off('wheel', handleUserInteraction);
        map.off('dragstart', handleUserInteraction);
      };
    }, [getMapInstance]);

    // Cleanup rotation on unmount
    useEffect(() => {
      return () => {
        if (rotationRef.current) {
          cancelAnimationFrame(rotationRef.current);
        }
      };
    }, []);

    return (
      <Box
        sx={{
          position: 'absolute',
          top: '80px', // Position directly under the header
          left: 20,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {/* Main Control Buttons */}
        <Paper
          elevation={4}
          sx={{
            background: 'rgba(24, 29, 38, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Zoom Controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff'
                    }
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff'
                    }
                  }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* 3D Toggle */}
            <Tooltip title={is3DMode ? (isRotating ? "Stop 3D Rotation" : "Switch to 2D") : "Start 3D Rotation"}>
              <IconButton
                size="small"
                onClick={handleToggle3D}
                sx={{
                  color: is3DMode ? (isRotating ? '#FF9800' : '#4CAF50') : 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 0,
                  animation: isRotating ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                    '100%': { opacity: 1 }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: is3DMode ? (isRotating ? '#FF9800' : '#4CAF50') : '#fff'
                  }
                }}
              >
                {is3DMode ? <View2DIcon fontSize="small" /> : <View3DIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Reset Bearing */}
            <Tooltip title="Reset to North">
              <IconButton
                size="small"
                onClick={handleResetBearing}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff'
                  }
                }}
              >
                <RotateLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Advanced Controls Toggle */}
            <Tooltip title="Advanced Controls">
              <IconButton
                size="small"
                onClick={() => setShowControls(!showControls)}
                sx={{
                  color: showControls ? '#2196F3' : 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: showControls ? '#2196F3' : '#fff'
                  }
                }}
              >
                <CompassIcon fontSize="small" />
              </IconButton>
            </Tooltip>

          </Box>
        </Paper>

        {/* Scale Display - Separate from navigation control */}
        <Paper
          elevation={4}
          sx={{
            background: 'rgba(24, 29, 38, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 'fit-content'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.7rem',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            {scale}
          </Typography>
        </Paper>

        {/* Advanced Controls Panel */}
        {showControls && (
          <Paper
            elevation={4}
            sx={{
              background: 'rgba(24, 29, 38, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: 2,
              minWidth: 200
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Bearing Control */}
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                  Bearing: {bearing.toFixed(0)}°
                </Typography>
                <Slider
                  value={bearing}
                  onChange={handleBearingChange}
                  min={-180}
                  max={180}
                  step={1}
                  sx={{
                    color: '#2196F3',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#2196F3',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#2196F3',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                />
              </Box>

              {/* Pitch Control */}
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                  Pitch: {pitch.toFixed(0)}°
                </Typography>
                <Slider
                  value={pitch}
                  onChange={handlePitchChange}
                  min={0}
                  max={60}
                  step={1}
                  sx={{
                    color: '#4CAF50',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#4CAF50',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#4CAF50',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                />
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };



  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (map && flyToTarget && flyToTrigger) {
      map.flyTo({ center: flyToTarget, zoom: flyToZoom ?? 15, essential: true });
    }
  }, [flyToTarget, flyToTrigger, flyToZoom]);

  // Look for where flyTo is being called and add validation
  useEffect(() => {
    if (manualGeometry && manualZoomRequest && mapRef.current) {
      try {
        if (manualGeometry.type === 'Point') {
          const center = manualGeometry.coordinates as [number, number];
          if (!isNaN(center[0]) && !isNaN(center[1])) {
            mapRef.current.flyTo({
              center: center,
              zoom: 12, // Zoom level for a 5km view
              duration: 1500,
              essential: true
            });
          }
        } else if (manualGeometry.type === 'Polygon') {
          const coords = manualGeometry.coordinates[0] as [number, number][];
          if (coords.length > 1) {
            const bounds = new mapboxgl.LngLatBounds();
            coords.forEach(coord => bounds.extend(coord));
            mapRef.current.fitBounds(bounds, {
              padding: 80,
              duration: 1500,
              maxZoom: 16
            });
          }
        }

        if (onManualZoomComplete) {
          setTimeout(() => onManualZoomComplete(), 1500);
        }
      } catch (error) {
        console.error('Error handling manual geometry zoom:', error);
        if (onManualZoomComplete) {
          onManualZoomComplete();
        }
      }
    }
  }, [manualGeometry, manualZoomRequest, onManualZoomComplete]);

  useEffect(() => {
    if (manualGeometry) {
      const feature = turf.feature(manualGeometry);
      const collection = turf.featureCollection([feature]);
      setManualGeoJson(collection);
    } else {
      setManualGeoJson({ type: 'FeatureCollection', features: [] });
    }
  }, [manualGeometry]);

  useEffect(() => {
    const validLayers = importedLayers.filter(layer => {
      const hasValidGeoJson = layer.geojson &&
        typeof layer.geojson === 'object' &&
        layer.geojson.type === 'FeatureCollection' &&
        Array.isArray(layer.geojson.features);
      return hasValidGeoJson;
    });
    setImportedLayersData(validLayers);
  }, [importedLayers]);

  // Zoom handlers effect
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    // Handle scroll zoom
    if (scrollZoomEnabled) {
      map.scrollZoom.enable();
    } else {
      map.scrollZoom.disable();
    }

    // Handle box zoom
    if (boxZoomActive) {
      // Enable box zoom - users can hold shift and drag to zoom to rectangle
      map.boxZoom.enable();
      // Add visual feedback for box zoom mode
      map.getContainer().style.cursor = 'crosshair';
    } else {
      map.boxZoom.disable();
      // Reset cursor
      map.getContainer().style.cursor = '';
    }
  }, [scrollZoomEnabled, boxZoomActive]);

  // Enhanced satellite imagery with multiple high-quality sources
  const mapStyle = getBasemapStyle(basemap);

  // Set different max zoom levels based on satellite mode to prevent "Map data not yet available"
  const getMaxZoomLevel = () => {
    if (basemap === 'satellite-a' || basemap === 'satellite-b' || basemap === 'satellite-c' || basemap === 'satellite-enhanced') {
      // Use satellite mode for zoom limits on satellite basemaps
      switch (satelliteMode) {
        case 'standard':
          return 18; // Conservative zoom for standard satellite
        case 'streets':
          return 19; // Slightly higher for satellite with labels
        case 'high-res':
          return 20; // Highest for enhanced imagery, but still limited
        default:
          return 18;
      }
    }
    return 22; // Full zoom for street maps
  };

  const maxZoomLevel = getMaxZoomLevel();
  const needsHighResLayer = (basemap === 'satellite-c' || basemap === 'satellite-enhanced' ||
    basemap === 'maxar-worldview' || basemap === 'airbus-pleiades' ||
    basemap === 'planet-skybox' || basemap === 'blacksky-constellation' ||
    basemap === 'sentinel-3' || basemap === 'sentinel-6' ||
    basemap === 'landsat-10' || basemap === 'capella-sar' || basemap === 'iceye-sar');

  if (!MAPBOX_TOKEN) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#ffcccb', border: '1px solid red' }}>
        <h2>Erreur de configuration</h2>
        <p>Le jeton d'accès Mapbox est manquant. Veuillez vous assurer que la variable d'environnement REACT_APP_MAPBOX_TOKEN est définie.</p>
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef} // Use the ref here
      id="map"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative'
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={mapStyle}
        initialViewState={viewState}
        onMove={onMove}
        onClick={onMapClick}
        onLoad={onMapLoadHandler}
        minZoom={2}
        maxZoom={maxZoomLevel}
        cursor="default"
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        doubleClickZoom={true}
      >
        <Source id="manual-preview" type="geojson" data={manualGeoJson}>
          <Layer id="manual-preview-fill" type="fill" paint={{ 'fill-color': '#1976d2', 'fill-opacity': 0.15 }} filter={['==', '$type', 'Polygon']} />
          <Layer id="manual-preview-outline" type="line" paint={{ 'line-color': '#1976d2', 'line-width': 2, 'line-opacity': 0.95 }} filter={['any', ['==', '$type', 'Polygon'], ['==', '$type', 'LineString']]} />
        </Source>

        {/* High-resolution satellite imagery overlay for enhanced detail */}
        {needsHighResLayer && (
          <Source
            id="high-res-satellite"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Esri, Maxar, Earthstar Geographics"
          >
            <Layer
              id="high-res-satellite-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.85 // Slightly transparent to blend with base satellite
              }}
            />
          </Source>
        )}

        {/* 2025 High-Resolution Commercial Satellite Providers */}
        {basemap === 'maxar-worldview' && (
          <Source
            id="maxar-worldview"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Maxar Technologies"
          >
            <Layer
              id="maxar-worldview-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.9
              }}
            />
          </Source>
        )}

        {basemap === 'airbus-pleiades' && (
          <Source
            id="airbus-pleiades"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Airbus Defence and Space"
          >
            <Layer
              id="airbus-pleiades-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.9
              }}
            />
          </Source>
        )}

        {basemap === 'planet-skybox' && (
          <Source
            id="planet-skybox"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Planet Labs"
          >
            <Layer
              id="planet-skybox-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.9
              }}
            />
          </Source>
        )}

        {basemap === 'blacksky-constellation' && (
          <Source
            id="blacksky-constellation"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© BlackSky"
          >
            <Layer
              id="blacksky-constellation-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.9
              }}
            />
          </Source>
        )}

        {/* SAR Imagery Providers */}
        {basemap === 'capella-sar' && (
          <Source
            id="capella-sar"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Capella Space"
          >
            <Layer
              id="capella-sar-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.8,
                'raster-hue-rotate': 180 // SAR imagery typically has different color characteristics
              }}
            />
          </Source>
        )}

        {basemap === 'iceye-sar' && (
          <Source
            id="iceye-sar"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© ICEYE"
          >
            <Layer
              id="iceye-sar-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.8,
                'raster-hue-rotate': 180 // SAR imagery typically has different color characteristics
              }}
            />
          </Source>
        )}

        {/* Latest Earth Observation Satellites */}
        {basemap === 'sentinel-3' && (
          <Source
            id="sentinel-3"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Sentinel-3 / ESA"
          >
            <Layer
              id="sentinel-3-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.85
              }}
            />
          </Source>
        )}

        {basemap === 'sentinel-6' && (
          <Source
            id="sentinel-6"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Sentinel-6 / ESA"
          >
            <Layer
              id="sentinel-6-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.85
              }}
            />
          </Source>
        )}

        {basemap === 'landsat-10' && (
          <Source
            id="landsat-10"
            type="raster"
            tiles={[
              'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            ]}
            tileSize={256}
            attribution="© Landsat 10 / USGS"
          >
            <Layer
              id="landsat-10-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.85
              }}
            />
          </Source>
        )}

        {/* MODIS 2025 Collections */}
        {basemap === 'modis-terra-2025' && (
          <Source
            id="modis-terra-2025"
            type="raster"
            tiles={[
              `${API_BASE_URL}/api/gee/tiles/{z}/{x}/{y}?collection=modis-terra-2025&startDate=2025-01-01&endDate=2025-12-31`
            ]}
            tileSize={256}
            attribution="© MODIS Terra 2025 / NASA"
          >
            <Layer
              id="modis-terra-2025-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.9
              }}
            />
          </Source>
        )}

        {basemap === 'modis-aqua-2025' && (
          <Source
            id="modis-aqua-2025"
            type="raster"
            tiles={[
              `${API_BASE_URL}/api/gee/tiles/{z}/{x}/{y}?collection=modis-aqua-2025&startDate=2025-01-01&endDate=2025-12-31`
            ]}
            tileSize={256}
            attribution="© MODIS Aqua 2025 / NASA"
          >
            <Layer
              id="modis-aqua-2025-layer"
              type="raster"
              paint={{
                'raster-opacity': 0.9
              }}
            />
          </Source>
        )}

        {/* IGN 1993 tileset overlay */}
        {basemap === 'ign-1993' && (
          <Source
            id="ign-1993-tileset"
            type="raster"
            tiles={[
              `https://api.mapbox.com/v4/eminbouka.d2codq9b/{z}/{x}/{y}.png?access_token=${MAPBOX_TOKEN}`
            ]}
            tileSize={256}
            attribution="© IGN 1993"
          >
            <Layer
              id="ign-1993-layer"
              type="raster"
              paint={{
                'raster-opacity': 1
              }}
            />
          </Source>
        )}

        {/* Google Earth Engine overlays - Using actual GEE data */}
        {(basemap === 'gee-sentinel-2' || basemap === 'gee-landsat-8' || basemap === 'gee-landsat-9' || basemap === 'gee-modis' || basemap === 'gee-spot' ||
          basemap === 'gee-sentinel-3' || basemap === 'gee-sentinel-6' || basemap === 'gee-landsat-10' ||
          basemap === 'gee-modis-terra-2025' || basemap === 'gee-modis-aqua-2025' ||
          basemap === 'gee-planet-skybox' || basemap === 'gee-blacksky-constellation') && (
            <Source
              id="gee-satellite"
              type="raster"
              tiles={[
                // Use actual GEE tile URLs from backend
                `${API_BASE_URL}/api/gee/tiles/{z}/{x}/{y}?collection=${basemap.replace('gee-', '')}&startDate=2024-01-01&endDate=2024-12-31`
              ]}
              tileSize={256}
              attribution={`© Google Earth Engine (${basemap.replace('gee-', '').toUpperCase()})`}
            >
              <Layer
                id="gee-satellite-layer"
                type="raster"
                paint={{
                  'raster-opacity': 1.0
                }}
              />
            </Source>
          )}

        {permanentLayers && visibleLayers && (
          <EnhancedPermanentLayers
            layers={permanentLayers}
            visibleLayers={visibleLayers}
            measureActive={measureActive}
            layerActionPanelOpen={layerActionPanelOpen}
            onLayerActionClose={onLayerActionClose}
          />
        )}

        {children}

        {/* Custom 3D Navigation Control */}
        <Box3DNavigationControl getMapInstance={getMapInstance} />



        {/* Imported layers (including CSV points) */}
        {importedLayersData.filter(layer => layer.visible).map(layer => (
          <Source key={layer.id} id={layer.id} type="geojson" data={layer.geojson}>
            <Layer
              id={`${layer.id}-points`}
              type="circle"
              paint={{
                'circle-radius': 3,
                'circle-color': '#ff4757',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1
              }}
              filter={['==', '$type', 'Point']}
            />
            {labelsVisible && (
              <Layer
                id={`${layer.id}-labels`}
                type="symbol"
                layout={{
                  'text-field': ['case',
                    ['has', 'name'], ['get', 'name'],
                    ['has', 'Name'], ['get', 'Name'],
                    ['has', 'NAME'], ['get', 'NAME'],
                    ''
                  ],
                  'text-font': ['Open Sans Bold'],
                  'text-offset': [0, 0.3],
                  'text-anchor': 'top',
                  'text-size': 9
                }}
                paint={{
                  'text-color': '#1a1a1a',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1.5
                }}
                filter={['==', '$type', 'Point']}
              />
            )}
            <Layer
              id={`${layer.id}-lines`}
              type="line"
              paint={{
                'line-color': '#ff4757',
                'line-width': 3
              }}
              filter={['==', '$type', 'LineString']}
            />
            <Layer
              id={`${layer.id}-polygons`}
              type="fill"
              paint={{
                'fill-color': '#ff4757',
                'fill-opacity': 0.3
              }}
              filter={['==', '$type', 'Polygon']}
            />
            <Layer
              id={`${layer.id}-polygon-outlines`}
              type="line"
              paint={{
                'line-color': '#ff4757',
                'line-width': 2
              }}
              filter={['==', '$type', 'Polygon']}
            />
          </Source>
        ))}

        {/* {importedLayersData.map(layer => (
            <Source key={layer.id} id={layer.id} type="geojson" data={layer.geojson}>
              <Layer 
                id={`${layer.id}-fill`} 
                type="fill" 
                paint={{ 'fill-color': '#6cc24a', 'fill-opacity': 0.4 }} 
              />
            </Source>
          ))} */}

        {pins && pins.map(pin => (
          <Marker key={pin.id} longitude={parseFloat(pin.lng)} latitude={parseFloat(pin.lat)} anchor="center">
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1569ef', border: '1px solid white' }} />
          </Marker>
        ))}

        {tempMarker && (
          <Marker longitude={tempMarker.lng} latitude={tempMarker.lat} anchor="center">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ff4757', border: '1px solid white' }} />
          </Marker>
        )}

        {/* Area markers for Area mode */}
        {areaMarkers && areaMarkers.map(marker => (
          <Marker key={marker.id} longitude={marker.lng} latitude={marker.lat} anchor="center">
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#2c8fdb',
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }} />
          </Marker>
        ))}
      </Map>

      {/* Satellite Imagery Information Overlay */}
      <SatelliteInfo
        satelliteMode={satelliteMode}
        visible={basemap === 'satellite-a' || basemap === 'satellite-b' || basemap === 'satellite-c' || basemap === 'satellite-enhanced'}
      />

    </div>
  );
});

export default React.memo(MapboxMap) as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<MapboxMapProps> & React.RefAttributes<MapboxMapHandle>
>;
